
/**
 * Service for retrieving title and description for Level 3 tests
 */
import { 
  puttingTests,
  chippingTests,
  wedgingTests,
  bunkerTests, 
  ironTests,
  fairwayWoodTests,
  driverTests 
} from "@/data/level3/tests";
import { getTestPoints as getPointsUtil } from "@/utils/level3/points";
import { getTestDistances as getDistancesUtil } from "@/utils/level3/distances";
import i18n from "@/lib/i18n";

interface Test {
  id: string;
  title: string;
  description: string;
  icon: string;
}

// Map test IDs to their categories for translation lookup
const testCategoryMap: Record<string, string> = {
  'putts-plat': 'putting',
  'putts-montee': 'putting',
  'putts-descente': 'putting',
  'putts-pente': 'putting',
  'putts-pente-droite': 'putting',
  'chipping-plat': 'chipping',
  'chipping-pente-droit': 'chipping',
  'chipping-pente-gauche': 'chipping',
  'chipping-roulee-plat': 'chipping',
  'chipping-roulee-pente': 'chipping',
  'chipping-roulee-pente-gauche': 'chipping',
  'wedging-cercles': 'wedging',
  'wedging-mid': 'wedging',
  'wedging-pente-gauche': 'wedging',
  'wedging-pente-droite': 'wedging',
  'wedging-mid-pente-gauche': 'wedging',
  'wedging-mid-pente-droite': 'wedging',
  'bunker-court': 'bunker',
  'bunker-long': 'bunker',
  'iron-precision': 'iron',
  'iron-green-attack-100': 'iron',
  'fairwayWood-precision': 'fairwayWood',
  'driver-precision': 'driver'
};

/**
 * Get the title for a test by its ID for Level 3
 */
export const getTestTitle = (testId: string): string => {
  const category = testCategoryMap[testId];
  if (category) {
    const translationKey = `level3.testData.${category}.${testId}.title`;
    return i18n.t(translationKey, { ns: 'training' });
  }
  
  // Fallback to original data if no translation found
  const allTests: Test[] = [
    ...puttingTests,
    ...chippingTests,
    ...wedgingTests,
    ...bunkerTests,
    ...ironTests,
    ...fairwayWoodTests,
    ...driverTests
  ];
  
  const test = allTests.find(test => test.id === testId);
  return test ? test.title : "Test inconnu";
};

/**
 * Get the description for a test by its ID for Level 3
 * @param testId The ID of the test
 */
export const getTestDescription = (testId: string): string => {
  const category = testCategoryMap[testId];
  if (category) {
    const translationKey = `level3.testData.${category}.${testId}.description`;
    return i18n.t(translationKey, { ns: 'training' });
  }
  
  // Fallback to original data if no translation found
  const allTests: Test[] = [
    ...puttingTests,
    ...chippingTests,
    ...wedgingTests,
    ...bunkerTests,
    ...ironTests,
    ...fairwayWoodTests,
    ...driverTests
  ];
  
  const test = allTests.find(test => test.id === testId);
  return test ? test.description : "Description non disponible";
};

/**
 * Get test points for Level 3 tests
 */
export const getTestPoints = getPointsUtil;

/**
 * Get test distances specific to Level 3
 */
export const getTestDistances = getDistancesUtil;
